import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect } from 'react';
import { Offcanvas, Spinner } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { privateApi } from '../../../api/api';
import { toast } from 'sonner';
import AsyncSelect from 'react-select/async';
import clsx from 'clsx';
import { IconX } from '@tabler/icons-react';

interface AssignUserToPropertyProps {
    show: boolean;
    onClose: () => void;
    selectedPropertyToAssign: string | null;
}

interface AssignUserForm {
    property_id: string;
    name: string;
    users: { value: string; label: string }[];
}

const AssignUserToProperty: React.FC<AssignUserToPropertyProps> = ({ show, onClose, selectedPropertyToAssign }) => {
    const { handleSubmit, formState: { errors }, control, reset, watch } = useForm<AssignUserForm>({
        // KOMENTAR: Memberikan defaultValues di sini adalah praktik yang baik untuk
        // memastikan form state memiliki struktur yang benar sejak awal.
        defaultValues: {
            users: []
        }
    });
    const propertyName = watch('name');

    const handleClose = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    const { data: propertyData, isFetching: isFetchingProperty, isSuccess: isPropertySuccess, isError: isPropertyError } = useQuery({
        queryKey: ['property', selectedPropertyToAssign],
        queryFn: () => privateApi.get(`/v4/property/${selectedPropertyToAssign}`).then((res) => res.data.data),
        enabled: !!selectedPropertyToAssign && show,
        retry: false,
    });

    useEffect(() => {
        if (propertyData) {
            reset({
                property_id: propertyData.id,
                name: propertyData.name,
                users: propertyData.users.map((user: { id: string; name: string }) => ({ value: user.id, label: user.name })),
            });
        }
    }, [propertyData, reset]);

    useEffect(() => {
        if (isPropertyError) {
            toast.error('Failed to load property details. Please try again.');
            handleClose();
        }
    }, [isPropertyError, handleClose]);

    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        // Tetap v3: /v4/user-property/sync memakai syncWithoutDetaching (payload property_ids[]),
        // sehingga menghapus user dari daftar tidak akan men-detach — behavior berbeda dari v3.
        mutationFn: (data: { property_id: string; user_ids: string[] }) => privateApi.post('/v3/user-property/sync', data),
        onError: (error: any) => { toast.error(error?.response?.data?.message || 'Failed to assign users.', { id: 'assign-user-to-property' }); },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['properties'] });
            handleClose();
            toast.success('Users assigned successfully!', { id: 'assign-user-to-property' });
        }
    });

    const onSubmit = useCallback((values: AssignUserForm) => {
        toast.loading('Assigning users...', { id: 'assign-user-to-property' });
        const payload = { property_id: values.property_id, user_ids: values.users.map((user) => user.value) };
        mutate(payload);
    }, [mutate]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement='end' style={{ width: '45%' }}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    {isPropertySuccess ? `Assign Users to '${propertyName}'` : 'Assign User to Property'}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="d-flex flex-column">
                {isFetchingProperty && (
                    <div className="d-flex justify-content-center align-items-center h-100">
                        <Spinner animation="border" />
                    </div>
                )}
                {isPropertySuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column flex-grow-1">
                        <div className="flex-grow-1">
                            <fieldset disabled={isPending}>
                                <div className="mb-3 row align-items-center">
                                    <label className="col-sm-3 col-form-label fw-medium">Property</label>
                                    <div className="col-sm-9">
                                        <input type='hidden' {...control.register('property_id')} />
                                        <input type="text" className="form-control" readOnly disabled {...control.register('name')} />
                                        <div className="form-text">Users will be assigned to this property.</div>
                                    </div>
                                </div>
                                <AsyncSelectUsers control={control} errors={errors} />
                            </fieldset>
                        </div>
                        <div className="mt-auto pt-3">
                            <button type="submit" className="btn btn-primary d-flex align-items-center" disabled={isPending}>
                                {isPending && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />}
                                {isPending ? 'Assigning...' : 'Assign Users'}
                            </button>
                        </div>
                    </form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default AssignUserToProperty;

// ===================================================================================
// KOMPONEN UNTUK MEMILIH PENGGUNA (DIPERBAIKI)
// ===================================================================================
interface AsyncSelectUsersProps {
    control: any;
    errors: any;
}

const AsyncSelectUsers: React.FC<AsyncSelectUsersProps> = ({ control, errors }) => {
    const loadOptions = async (inputValue: string) => {
        try {
            const response = await privateApi.get('/v4/user', { params: { search: inputValue, limit: 15 } });
            return response.data.data.map((user: { id: string; name: string }) => ({ value: user.id, label: user.name }));
        } catch (error) {
            console.error("Failed to fetch users:", error);
            return [];
        }
    };

    return (
        <div className="mb-3 row">
            <label className="col-sm-3 col-form-label fw-medium required">Users</label>
            <div className="col-sm-9">
                <Controller
                    name="users"
                    control={control}
                    rules={{ required: 'Please select at least one user.' }}
                    render={({ field }) => {
                        // KOMENTAR: Ini adalah perbaikan utama. Kita membuat variabel 'currentUsers'
                        // yang dijamin selalu berupa array. Jika field.value undefined, ia akan menjadi [].
                        const currentUsers = field.value || [];

                        const handleSelectUser = (selectedOption: { value: string; label: string } | null) => {
                            if (!selectedOption) return;
                            // KOMENTAR: Gunakan 'currentUsers' yang aman.
                            const isAlreadySelected = currentUsers.some((user: { value: string }) => user.value === selectedOption.value);
                            if (!isAlreadySelected) {
                                // KOMENTAR: Gunakan 'currentUsers' yang aman.
                                field.onChange([...currentUsers, selectedOption]);
                            } else {
                                toast.info(`'${selectedOption.label}' is already selected.`);
                            }
                        };

                        const handleRemoveUser = (userIdToRemove: string) => {
                            // KOMENTAR: Gunakan 'currentUsers' yang aman.
                            const newUsers = currentUsers.filter((user: { value: string }) => user.value !== userIdToRemove);
                            field.onChange(newUsers);
                        };

                        return (
                            <>
                                <AsyncSelect
                                    value={null}
                                    loadOptions={loadOptions}
                                    onChange={handleSelectUser}
                                    placeholder="Type to search and add users..."
                                    // KOMENTAR: Gunakan 'currentUsers' yang aman.
                                    className={clsx(errors.users && currentUsers.length === 0 && 'is-invalid')}
                                    classNamePrefix="react-select"
                                    noOptionsMessage={({ inputValue }) => inputValue ? `No users found for "${inputValue}"` : 'Start typing...'}
                                />
                                {/* KOMENTAR: Gunakan 'currentUsers' yang aman. */}
                                {errors.users && currentUsers.length === 0 && (
                                    <div className="invalid-feedback d-block">{errors.users.message as string}</div>
                                )}

                                <div className="border rounded mt-2 p-2" style={{ minHeight: '100px', maxHeight: '250px', overflowY: 'auto' }}>
                                    {/* KOMENTAR: Gunakan 'currentUsers' yang aman. */}
                                    {currentUsers.length === 0 ? (
                                        <p className="text-muted text-center m-0 p-3">No users selected.</p>
                                    ) : (
                                        <ul className="list-unstyled mb-0">
                                            {/* KOMENTAR: Gunakan 'currentUsers' yang aman. */}
                                            {currentUsers.map((user: { value: string; label: string }) => (
                                                <li key={user.value} className="d-flex justify-content-between align-items-center bg-light-subtle rounded p-2 mb-1">
                                                    <span>{user.label}</span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-ghost-danger"
                                                        onClick={() => handleRemoveUser(user.value)}
                                                        title={`Remove ${user.label}`}
                                                    >
                                                        <IconX size={16} />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </>
                        );
                    }}
                />
            </div>
        </div>
    );
};